import * as XLSX from 'xlsx';
import { fetchIncotermTimeline } from '../../../Components/QuantityTable/utils/quantityDeliveryUtils';
import {
  getIncotermBasesFromParcel,
  fetchQuantityInformationTargets,
  buildLpgIncotermTableModel,
  formatParcelSizes,
  fetchAllCargoRatioSelections,
  fetchCargoRatioOptions,
  getConfirmedRatiosFromSelections,
  fetchIncotermAlphaMap,
} from '../component/HpsParcel/lpg/utils';
import { resolveInformationPrompt } from './informationPrompt';

// Urutan delivery unit di UI = qtyDeliveryData (createdDate asc, lihat HpsForm).
// Export get-delivery-parcel bisa berbeda — samakan indeksnya sebelum build row.
export const orderParcelsByDisplayOrder = (
  parcels = [],
  deliveryItems = []
) => {
  const orderById = new Map(
    (deliveryItems || []).map((item, index) => [String(item?.id), index])
  );
  return [...parcels].sort((a, b) => {
    const oa = orderById.get(String(a?.id)) ?? Number.MAX_SAFE_INTEGER;
    const ob = orderById.get(String(b?.id)) ?? Number.MAX_SAFE_INTEGER;
    if (oa !== ob) return oa - ob;
    return String(a?.id ?? '').localeCompare(String(b?.id ?? ''));
  });
};

// Index urutan baris qtyModeDeliveryIncoterm dari get-delivery-detail:
// groups.flatMap(group => listData) — FOB diperluas per incotermFOBLocations.
const buildDetailOrderIndex = groups => {
  const detailOrder = [];
  const locOrder = new Map();
  for (const group of groups || []) {
    for (const details of group?.listData || []) {
      const detailId = String(details?.id ?? '');
      if (!detailId) continue;
      detailOrder.push(detailId);
      locOrder.set(
        detailId,
        (Array.isArray(details?.incotermFOBLocations)
          ? details.incotermFOBLocations
          : []
        ).map(loc => ({
          id: String(loc?.id ?? ''),
          name: String(loc?.name ?? '')
            .trim()
            .toLowerCase(),
        }))
      );
    }
  }
  return { detailOrder, locOrder };
};

const buildFobLocationRank = (entries = []) => {
  const byId = new Map();
  const byName = new Map();
  entries.forEach((entry, index) => {
    if (entry.id && !byId.has(entry.id)) byId.set(entry.id, index);
    if (entry.name && !byName.has(entry.name)) byName.set(entry.name, index);
  });
  return loc => {
    const id = String(loc?.id ?? '');
    if (id && byId.has(id)) return byId.get(id);
    const name = String(loc?.name ?? '')
      .trim()
      .toLowerCase();
    return name && byName.has(name)
      ? byName.get(name)
      : Number.MAX_SAFE_INTEGER;
  };
};

// Reorder listData + incotermFOBLocations tiap parcel mengikuti urutan
// get-delivery-detail (sama dengan tabel Incoterm di UI).
export const reorderParcelsByDetail = (parcels = [], indexByTermId = {}) =>
  parcels.map(parcel => {
    const index = indexByTermId[String(parcel?.id ?? '')];
    if (!index?.detailOrder?.length) return parcel;

    const pos = new Map(index.detailOrder.map((id, i) => [id, i]));
    const sortedListData = (parcel.listData || [])
      .map((item, origIdx) => ({ item, origIdx }))
      .sort((a, b) => {
        const pa = pos.get(String(a.item?.id ?? '')) ?? Number.MAX_SAFE_INTEGER;
        const pb = pos.get(String(b.item?.id ?? '')) ?? Number.MAX_SAFE_INTEGER;
        return pa - pb || a.origIdx - b.origIdx;
      })
      .map(({ item }) => {
        const locs = item?.incotermFOBLocations;
        if (!Array.isArray(locs) || !locs.length) return item;
        const entries = index.locOrder.get(String(item?.id ?? ''));
        if (!entries?.length) return item;
        const rank = buildFobLocationRank(entries);
        const sortedLocs = locs
          .map((loc, origIdx) => ({ loc, origIdx }))
          .sort((a, b) => rank(a.loc) - rank(b.loc) || a.origIdx - b.origIdx)
          .map(({ loc }) => loc);
        return { ...item, incotermFOBLocations: sortedLocs };
      });

    return { ...parcel, listData: sortedListData };
  });

export const buildDetailOrderIndexFromGroups = buildDetailOrderIndex;

const PARCEL_EXPORT_COLUMNS = [
  { header: 'ID', key: 'id' },
  { header: 'Incoterm Location ID', key: 'incotermLocationId' },
  { header: 'Delivery Unit', key: 'deliveryUnit' },
  { header: 'Product Name', key: 'productName' },
  { header: 'Product Type', key: 'productTypeName' },
  { header: 'Parcel Size', key: 'parcelSize' },
  { header: 'Incoterm', key: 'incoterm' },
  { header: 'Route', key: 'route' },
  { header: 'Loading Port', key: 'loadingPort' },
  { header: 'Discharge Qty 1', key: 'dischargeQty1' },
  { header: 'Port 1', key: 'port1' },
  { header: 'ALD/ADD 1', key: 'aldAdd1' },
  { header: 'Discharge Qty 2', key: 'dischargeQty2' },
  { header: 'Port 2', key: 'port2' },
  { header: 'ALD/ADD 2', key: 'aldAdd2' },
  { header: 'Discharge Qty 3', key: 'dischargeQty3' },
  { header: 'Port 3', key: 'port3' },
  { header: 'ALD/ADD 3', key: 'aldAdd3' },
  { header: 'Information', key: 'information' },
  { header: 'Alpha Dyed', key: 'alphaDyed' },
  { header: 'Alpha Undyed', key: 'alphaUndyed' },
];

// Nilai alpha HPS datang dari API sebagai null bila belum pernah diisi; tampilkan '-'
// supaya konsisten dengan kolom lain dan supaya sel kosong tidak terbaca sebagai 0.
const alphaValue = value =>
  value === null || value === undefined || value === '' ? '-' : value;

// Variant the parcel did not ask for (gasoline: Dyed or Undyed only). The
// upload reads N/A as empty.
const NOT_REQUESTED = 'N/A';

const isFob = row =>
  String(row?.incoterm ?? '')
    .trim()
    .toUpperCase() === 'FOB';

export const buildParcelExportRows = (parcels = []) => {
  if (!parcels?.length) return [];
  return parcels.flatMap(parcel => {
    const deliveryUnit = parcel.deliveryUnit || '-';
    const productName = parcel.productName || '-';
    const productTypeName = parcel.productTypeName || '-';
    const information = resolveInformationPrompt(parcel.infoPrompt);
    const listData = parcel.listData || [];
    const dyedValue = value =>
      parcel.isDyed === false ? NOT_REQUESTED : alphaValue(value);
    const undyedValue = value =>
      parcel.isUndyed === false ? NOT_REQUESTED : alphaValue(value);

    return listData.flatMap(item => {
      if (isFob(item)) {
        const fobLocations = item.incotermFOBLocations || [];
        if (!fobLocations.length) {
          return [
            {
              id: item.id || '-',
              incotermLocationId: '-',
              deliveryUnit,
              productName,
              productTypeName,
              information,
              parcelSize: item.deliveryBasis ?? '-',
              incoterm: item.incoterm || '-',
              route: item.route || '-',
              loadingPort: '-',
              dischargeQty1: '-',
              port1: '-',
              aldAdd1: '-',
              dischargeQty2: '-',
              port2: '-',
              aldAdd2: '-',
              dischargeQty3: '-',
              port3: '-',
              aldAdd3: '-',
              alphaDyed: dyedValue(item.alphaDyedHps),
              alphaUndyed: undyedValue(item.alphaUndyedHps),
            },
          ];
        }
        return fobLocations.map(loc => ({
          id: item.id || '-',
          incotermLocationId: loc.id || '-',
          deliveryUnit,
          productName,
          productTypeName,
          information,
          parcelSize: loc.deliveryBasis ?? item.deliveryBasis ?? '-',
          incoterm: item.incoterm || '-',
          route: item.route || '-',
          loadingPort: loc.name || '-',
          dischargeQty1: '-',
          port1: '-',
          aldAdd1: loc.addAld ?? item.addAld ?? '-',
          dischargeQty2: '-',
          port2: '-',
          aldAdd2: '-',
          dischargeQty3: '-',
          port3: '-',
          aldAdd3: '-',
          alphaDyed: dyedValue(loc.alphaDyedHps),
          alphaUndyed: undyedValue(loc.alphaUndyedHps),
        }));
      }

      return [
        {
          id: item.id || '-',
          incotermLocationId: ' ',
          deliveryUnit,
          productName,
          productTypeName,
          information,
          parcelSize: item.deliveryBasis ?? '-',
          incoterm: item.incoterm || '-',
          route: '-',
          loadingPort: '-',
          dischargeQty1: item.parcelSize ?? '-',
          port1: item.port1 || '-',
          aldAdd1: item.addAld || '-',
          dischargeQty2: item.parcelSize2 ?? '-',
          port2: item.port2 || '-',
          aldAdd2: item.addAld2 || '-',
          dischargeQty3: item.parcelSize3 ?? '-',
          port3: item.port3 || '-',
          aldAdd3: item.addAld3 || '-',
          alphaDyed: dyedValue(item.alphaDyedHps),
          alphaUndyed: undyedValue(item.alphaUndyedHps),
        },
      ];
    });
  });
};

const pad = n => String(n).padStart(2, '0');

export const downloadParcelExcel = (
  parcels,
  procurementNumber,
  options = {}
) => {
  const { singleAlpha = false } = options;
  const rows = buildParcelExportRows(parcels);
  if (!rows.length) {
    return false;
  }

  const now = new Date();
  const dateStr = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  const filename = `Parcel_${procurementNumber || 'data'}_${dateStr}.xlsx`;

  // Avtur / High Speed Diesel: satu kolom "Alpha" (nilai Alpha Dyed)
  // menggantikan pasangan Alpha Dyed + Alpha Undyed.
  const columns = singleAlpha
    ? PARCEL_EXPORT_COLUMNS.map(col =>
        col.key === 'alphaDyed' ? { ...col, header: 'Alpha' } : col
      ).filter(col => col.key !== 'alphaUndyed')
    : PARCEL_EXPORT_COLUMNS;

  const headers = columns.map(col => col.header);
  const data = rows.map(row =>
    columns.map(col => {
      const value = row[col.key] ?? '-';
      // Kolom "Alpha" tunggal tidak memakai penanda N/A dyed/undyed.
      if (singleAlpha && col.key === 'alphaDyed' && value === NOT_REQUESTED) {
        return '-';
      }
      return value;
    })
  );

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);

  const colWidths = columns.map((col, idx) => {
    if (idx < 2) {
      return { wch: 0, hidden: true };
    }
    const maxLen = Math.max(
      col.header.length,
      ...rows.map(row => String(row[col.key] ?? '-').length)
    );
    return { wch: Math.min(maxLen + 2, 40) };
  });
  worksheet['!cols'] = colWidths;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Parcel');
  XLSX.writeFile(workbook, filename);

  return true;
};

const LPG_EXPORT_COLUMNS = [
  { header: 'id', key: 'cargoRatioId' },
  { header: 'quantityIncotermFK', key: 'quantityIncotermFK' },
  { header: 'quantityInformationFK', key: 'quantityInformationFK' },
  { header: 'Delivery Unit', key: 'deliveryUnit' },
  { header: 'Product Name', key: 'productName' },
  { header: 'Product Type', key: 'productTypeName' },
  { header: 'ALD/ADD', key: 'aldAdd' },
  { header: 'Incoterm', key: 'incoterm' },
  { header: 'Port 1', key: 'port1' },
  { header: 'Port 2', key: 'port2' },
  { header: 'Port 3', key: 'port3' },
  { header: 'Cargo Ratio', key: 'cargoRatioLabel' },
  { header: 'Parcel Size', key: 'parcelSize' },
  { header: 'Information', key: 'information' },
  { header: 'Alpha', key: 'alpha' },
];

export const enrichParcelWithTimeline = async (parcel, token) => {
  try {
    const { rawItems, timelineItems } = await fetchIncotermTimeline(
      parcel.id,
      token
    );
    return {
      ...parcel,
      incotermTimelineItems: timelineItems,
      incotermRawItems: rawItems,
    };
  } catch {
    return { ...parcel, incotermTimelineItems: [], incotermRawItems: [] };
  }
};

export const downloadParcelExcelLpg = async (
  parcels,
  procurementId,
  procurementNumber,
  token
) => {
  if (!parcels?.length) return false;

  const enrichedParcels = await Promise.all(
    parcels.map(parcel => enrichParcelWithTimeline(parcel, token))
  );

  const allRows = [];

  for (const parcel of enrichedParcels) {
    const incotermBases = getIncotermBasesFromParcel(parcel);

    if (!incotermBases.length) continue;

    const quantityTargets = await fetchQuantityInformationTargets(
      procurementId,
      parcel.id
    );

    const parcelSizes = formatParcelSizes(quantityTargets, 'MT');

    if (!parcelSizes.length) continue;

    const quantityFks = quantityTargets.map(t => t.quantityInformationFk);
    const selections = await fetchAllCargoRatioSelections(quantityFks);
    const ratioOptions = await fetchCargoRatioOptions();
    const confirmedRatios = getConfirmedRatiosFromSelections(
      selections,
      ratioOptions
    );

    const incotermAlphaMap = await fetchIncotermAlphaMap(parcel.id);

    const { rows } = buildLpgIncotermTableModel({
      incotermBases,
      parcelSizes,
      quantityTargets,
      selections,
      confirmedRatios,
      incotermAlphaMap,
    });

    const normalizeLabel = label => (label || '').replace(/\s+/g, '');

    const extractPort = loc => {
      if (!loc) return '';
      if (typeof loc === 'string') return loc;
      return (
        loc?.location?.name ??
        loc?.location?.Name ??
        loc?.Location?.name ??
        loc?.Location?.Name ??
        loc?.name ??
        loc?.Name ??
        ''
      );
    };

    const cargoRatioLookup = new Map();
    const rawItems = parcel.incotermRawItems || [];

    for (const item of rawItems) {
      const ports = [
        extractPort(item?.incotermLocation),
        extractPort(item?.incotermLocation2),
        extractPort(item?.incotermLocation3),
      ].filter(Boolean);

      const effectivePorts = ports.length ? ports : [''];

      for (const cr of item?.cargoRatios || []) {
        for (const port of effectivePorts) {
          const key = `${port}__${normalizeLabel(cr.cargoRatioLabel)}__${cr.quantityInformationFK}`;
          cargoRatioLookup.set(key, {
            id: cr.id,
            quantityIncotermFK: cr.quantityIncotermFK,
            quantityInformationFK: cr.quantityInformationFK,
          });
        }
      }
    }

    const qFkToParcelSize = new Map();
    quantityTargets.forEach((t, idx) => {
      qFkToParcelSize.set(t.quantityInformationFk, parcelSizes[idx]);
    });

    rows.forEach(row => {
      let match = null;
      const qFk = [...qFkToParcelSize.entries()].find(
        ([, ps]) => ps === row.parcelSize
      )?.[0];

      if (qFk) {
        const lookupKey = `${row.port1}__${normalizeLabel(row.cargoRatioLabel)}__${qFk}`;
        match = cargoRatioLookup.get(lookupKey);
      }

      allRows.push({
        cargoRatioId: match?.id || '-',
        quantityIncotermFK: match?.quantityIncotermFK || '-',
        quantityInformationFK: match?.quantityInformationFK || '-',
        deliveryUnit: parcel.deliveryUnit || '-',
        productName: parcel.productName || '-',
        productTypeName: parcel.productTypeName || '-',
        information: resolveInformationPrompt(parcel.infoPrompt),
        aldAdd: row.port1AldAdd || row.aldAdd || '-',
        incoterm: row.incotermRoute || row.incoterm || '-',
        port1: row.port1 || row.port || '-',
        port2: row.port2 || '-',
        port3: row.port3 || '-',
        cargoRatioLabel: row.cargoRatioLabel || '-',
        parcelSize: row.parcelSize || '-',
        alpha: row.alpha ?? 0,
      });
    });
  }

  if (!allRows.length) return false;

  const now = new Date();
  const dateStr = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;
  const filename = `Parcel_LPG_${procurementNumber || 'data'}_${dateStr}.xlsx`;

  const headers = LPG_EXPORT_COLUMNS.map(col => col.header);
  const data = allRows.map(row =>
    LPG_EXPORT_COLUMNS.map(col => row[col.key] ?? '-')
  );

  const worksheet = XLSX.utils.aoa_to_sheet([headers, ...data]);

  const colWidths = LPG_EXPORT_COLUMNS.map((col, idx) => {
    if (idx < 3) {
      return { wch: 0, hidden: true };
    }
    const maxLen = Math.max(
      col.header.length,
      ...allRows.map(row => String(row[col.key] ?? '-').length)
    );
    return { wch: Math.min(maxLen + 2, 40) };
  });
  worksheet['!cols'] = colWidths;

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Parcel LPG');
  XLSX.writeFile(workbook, filename);

  return true;
};
